import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCopy } from "@/lib/useCopyToClipBoard";
import { BsBank } from "react-icons/bs";
import { useAuthState } from "@/context";
import Img2 from "../../../../assets/qr.png";
import { CopyIcon, CopyIcon2 } from "@/assets";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCryptoPayInAddress, getFiatPayIn } from "@/services/Home.service";
import { Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import Selector from "@/components/Selector";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";

const FundAccount = () => {
  const [selectedCurrency, setSelectedCurrency] = useState({});
  const [selectedNetwork, setSelectedNetwork] = useState({});
  const { useCurrencies } = useAuthState();
  const { copyToClipboard } = useCopy();
  const currenciesList = useCurrencies()?.data?.data?.data?.map((i) => {
    return {
      label: `${i?.currency?.name} (${i?.currency?.code})`,
      value: i?.currency?.code,
      icon: i?.currency?.icon,
      type: i?.currency?.type,
    };
  });

  const fiatCheck =
    selectedCurrency?.label === undefined
      ? false
      : selectedCurrency?.type === "fiat"
      ? true
      : false;
  const cryptoCheck =
    selectedCurrency?.label !== undefined && selectedCurrency?.type === "crypto"
      ? true
      : false;
  const fiat = useQuery({
    queryKey: ["fiat"],
    queryFn: async () => await getFiatPayIn(selectedCurrency?.value),
    refetchOnMount: "always",
    enabled: !!fiatCheck,
  });
  const walletAddress = useQuery({
    queryKey: ["walletAddy", selectedNetwork?.value],
    queryFn: async () =>
      await getCryptoPayInAddress(
        selectedCurrency?.value,
        selectedNetwork?.value
      ),
    refetchOnMount: "always",
    enabled: !!cryptoCheck,
  });
  return (
    <CustomModal
      button={
        <Button className="flex gap-2 items-center">
          <Plus />
          Fund account
        </Button>
      }
      size="sm"
    >
      <div className="flex flex-col gap-8">
        <DialogHeader className="bg-off-white-1 py-6 px-8 rounded-t-[16px] flex flex-row items-center gap-4">
          <div className="w-12 h-12 rounded-full flex justify-center items-center bg-off-white-3 text-primary-3">
            <BsBank />
          </div>
          <div className="grid gap-[2px]">
            <DialogTitle className="text-lg font-medium text-primary-3">
              Fund account
            </DialogTitle>

            <DialogDescription className="text-sm text-gray-80">
              Top up your available balance{" "}
            </DialogDescription>
          </div>
        </DialogHeader>
      </div>
      <div className="px-8 py-3 grid gap-6">
        <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
          <Label className="text-xs text-gray-70">Asset</Label>
          <Selector
            options={currenciesList ?? []}
            selected={selectedCurrency}
            setSelected={setSelectedCurrency}
          />
        </div>
        {selectedCurrency?.type === "crypto" && (
          <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
            <Label className="text-xs text-gray-70">Select Network</Label>
            <Selector
              options={[
                { label: "BEP-20", value: "bep20" },
                { label: "TRC-20", value: "trc20" },
                { label: "ERC-20", value: "erc20" },
              ]}
              selected={selectedNetwork}
              setSelected={setSelectedNetwork}
            />
          </div>
        )}
        <Separator orientation="horizontal" className="h-[1px] bg-border" />

        <div className="grid gap-5">
          <h4 className="text-gray-80 text-sm font-medium">
            Deposit information
          </h4>

          {selectedCurrency?.label === undefined ? null : (
            <>
              {selectedCurrency?.type === "crypto" ? (
                <div className="gap-6 flex flex-col p-6 rounded-[8px] border-[1px] border-off-white">
                  <img src={Img2} className="mx-auto w-[136px] h-[136px]" />
                  <div className="flex text-xs text-gray-80 gap-3 items-center">
                    <div className="w-full">
                      <Separator
                        orientation="horizontal"
                        className="h-[1px] w-full bg-border"
                      />
                    </div>
                    Or
                    <div className="w-full">
                      <Separator
                        orientation="horizontal"
                        className="h-[1px] w-full bg-border"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {walletAddress?.isFetching ? (
                      <Skeleton className="h-12 w-[120px]" />
                    ) : (
                      <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-12 border-[#EBE9EC] justify-between">
                        <Label className="text-xs text-gray-70">
                          Copy wallet address
                        </Label>
                        <Input
                          className="p-0 border-none h-4 bg-transparent"
                          disabled
                          defaultValue={
                            walletAddress?.data?.data?.data?.address
                          }
                        />
                      </div>
                    )}
                    <Button
                      className="h-12 p-4 flex items-center gap-[5px]"
                      onClick={() =>
                        copyToClipboard(
                          walletAddress?.data?.data?.data?.address
                        )
                      }
                    >
                      Copy
                      <CopyIcon />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="grid">
                      <p className="text-gray-80 text-xs">Bank Name</p>
                      {fiat?.isFetching ? (
                        <Skeleton className="h-[14px] w-[120px]" />
                      ) : (
                        <h4 className="text-primary-3 text-sm font-medium">
                          {fiat?.data?.data?.data?.bankName}
                        </h4>
                      )}
                    </div>
                    <Button
                      className="p-0 h-0"
                      variant="ghost"
                      onClick={() =>
                        copyToClipboard(fiat?.data?.data?.data?.bankName)
                      }
                    >
                      <CopyIcon2 />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="grid">
                      <p className="text-gray-80 text-xs">Account Number</p>
                      {fiat?.isFetching ? (
                        <Skeleton className="h-[14px] w-[120px]" />
                      ) : (
                        <h4 className="text-primary-3 text-sm font-medium">
                          {fiat?.data?.data?.data?.accountNumber}
                        </h4>
                      )}
                    </div>
                    <Button
                      className="p-0 h-0"
                      variant="ghost"
                      onClick={() =>
                        copyToClipboard(fiat?.data?.data?.data?.accountNumber)
                      }
                    >
                      <CopyIcon2 />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="grid">
                      <p className="text-gray-80 text-xs">Account Name</p>
                      {fiat?.isFetching ? (
                        <Skeleton className="h-[14px] w-[120px]" />
                      ) : (
                        <h4 className="text-primary-3 text-sm font-medium">
                          {fiat?.data?.data?.data?.accountName}
                        </h4>
                      )}
                    </div>
                    <Button
                      className="p-0 h-0"
                      variant="ghost"
                      onClick={() =>
                        copyToClipboard(fiat?.data?.data?.data?.accountName)
                      }
                    >
                      <CopyIcon2 />
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default FundAccount;
